import { PageHead, Panel, StatCard, StatRow, Pill, Table, Row, Cell, btn, field as fieldClass, Modal, FormField, ConfirmModal } from './AdminUI';
import { buildCsv } from '../../utils/exportCsv';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, X, Loader2, Search, Hash, AlertTriangle, Percent, Layers, Download, ShieldCheck } from 'lucide-react';
import { API_ENDPOINTS, apiCall } from '../../config/api';
import { invalidateServices } from '../../config/servicesSource';

// Services and their HSC / GST, as the invoice form's Description dropdown
// reads them. Admins and super admins maintain the list here.
const EMPTY = { label: '', hsn: '', value: '' };

// Every service is taxed at the same rate, so it is not something anyone sets.
const GST_RATE = '18';

// The invoice form matches a saved invoice line back to a service by `value`,
// so each one needs a stable key. It is derived from the name and kept out of
// the UI — nobody has to think about it, and nothing can collide.
const slugify = (label) =>
  String(label || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 100);

const uniqueKey = (label, taken) => {
  const base = slugify(label) || 'service';
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
};

const HsnCodeManager = ({ search: searchProp, onSearch }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // The console's topbar can drive this instead, so the page's own box and
  // the one up there are the same search rather than two that disagree.
  const [ownSearch, setOwnSearch] = useState('');
  const controlled = typeof searchProp === 'string';
  const search = controlled ? searchProp : ownSearch;
  const setSearch = controlled ? (onSearch || (() => {})) : setOwnSearch;
  const [editing, setEditing] = useState(null);   // { id?, label, hsn, value } — value is the hidden key
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null); // the row awaiting confirmation
  const [deletingBusy, setDeletingBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiCall(API_ENDPOINTS.HSN_CODES, { method: 'GET' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.success !== false) {
        setRows(Array.isArray(data?.data) ? data.data : []);
      } else {
        toast.error(data?.message || 'Could not load HSC codes');
      }
    } catch {
      toast.error('Could not reach the server');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      [r.label, r.hsn].some(v => String(v ?? '').toLowerCase().includes(q))
    );
  }, [rows, search]);

  const openAdd = () => { setEditing({ ...EMPTY }); setErrors({}); };
  const openEdit = (row) => {
    setEditing({ id: row.id, label: row.label || '', hsn: row.hsn || '', value: row.value || '' });
    setErrors({});
  };
  const closeForm = () => { if (!saving) { setEditing(null); setErrors({}); } };

  const set = (field, v) => {
    setEditing(prev => ({ ...prev, [field]: v }));
    setErrors(prev => ({ ...prev, [field]: '', api: '' }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    const label = editing.label.trim();
    const hsn = editing.hsn.trim();

    if (label.length < 2) next.label = 'Enter the service name';
    if (!/^\d{4,8}$/.test(hsn)) next.hsn = 'HSC is 4–8 digits';
    if (Object.keys(next).length) { setErrors(next); return; }

    // An existing service keeps the key it was saved with, so invoices already
    // raised against it stay linked; a new one gets a fresh, unused key.
    const taken = new Set(rows.filter(r => r.id !== editing.id).map(r => r.value));
    const value = editing.id ? (editing.value || uniqueKey(label, taken)) : uniqueKey(label, taken);
    const gst = GST_RATE;

    setSaving(true);
    try {
      const isEdit = Boolean(editing.id);
      const res = await apiCall(isEdit ? `${API_ENDPOINTS.HSN_CODES}/${editing.id}` : API_ENDPOINTS.HSN_CODES, {
        method: isEdit ? 'PATCH' : 'POST',
        body: JSON.stringify({ label, value, hsn, gst }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        setErrors({ api: data?.message || `Could not save (HTTP ${res.status})` });
        return;
      }
      toast.success(isEdit ? 'HSC code updated' : 'HSC code added');
      setEditing(null);
      invalidateServices();   // the invoice form reads this list
      load();
    } catch {
      setErrors({ api: 'Could not reach the server' });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeletingBusy(true);
    try {
      const res = await apiCall(`${API_ENDPOINTS.HSN_CODES}/${deleting.id}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        toast.error(data?.message || 'Could not delete');
        return;
      }
      toast.success('HSC code deleted');
      setDeleting(null);
      invalidateServices();
      load();
    } catch {
      toast.error('Could not reach the server');
    } finally {
      setDeletingBusy(false);
    }
  };

  const field = (name, label, props = {}) => (
    <div className={props.wide ? 'sm:col-span-2' : ''}>
      <label className="flex items-baseline gap-1.5 mb-1.5">
        <span className="text-[12px] font-bold text-slate-700 dark:text-slate-300">{label}</span>
        <span className="text-rose-500">*</span>
      </label>
      <input
        value={editing[name]}
        onChange={(e) => set(name, props.transform ? props.transform(e.target.value) : e.target.value)}
        disabled={saving}
        {...props.input}
        className={`${fieldClass} ${
          errors[name] ? 'border-rose-400' : ''
        }`}
      />
      {errors[name]
        ? <p className="mt-1 text-xs text-red-500 dark:text-red-400">{errors[name]}</p>
        : props.hint && <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{props.hint}</p>}
    </div>
  );

  const distinctCodes = new Set(rows.map((r) => String(r.hsn || '').trim()).filter(Boolean)).size;

  const exportCatalog = () => {
    const body = rows.map((r, i) => [i + 1, r.label, r.hsn, `${GST_RATE}%`]);
    const blob = new Blob([buildCsv(['S.No', 'Service Name', 'HSC Code', 'GST %'], body)], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `hsc_codes_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div>
      <PageHead
        eyebrow="Customs & finance hub · HSC directory"
        title="HSC Codes & Tax Directory"
        badge={<Pill tone="blue">{rows.length} tariff mappings</Pill>}
        subtitle="The services the invoice Description list offers, with the HSC code and GST rate each one bills at."
        actions={(
          <>
            <button type="button" className={btn.quiet} onClick={exportCatalog}>
              <Download size={16} /> Export catalog
            </button>
            <button type="button" className={btn.primary} onClick={openAdd}>
              <Plus size={16} /> Add HSC code
            </button>
          </>
        )}
      />

      <StatRow>
        <StatCard label="Total tariff codes" value={rows.length} icon={Hash} tone="blue"
          note="Available on every invoice" />
        <StatCard label="Standard GST rate" value={`${GST_RATE}%`} icon={Percent} tone="violet"
          note="Fixed for all services" />
        <StatCard label="Distinct HSC codes" value={distinctCodes} icon={Layers} tone="emerald"
          note={distinctCodes === rows.length ? 'One per service' : 'Some services share a code'} />
        <StatCard label="Showing" value={filtered.length} icon={Search} tone="amber"
          note={search.trim() ? `Matching “${search.trim()}”` : 'No filter applied'} />
      </StatRow>

      <Panel className="overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 px-4 sm:px-5 py-4 border-b border-slate-100 dark:border-slate-800">
          {/* The console's topbar carries the search; only a standalone use of
              this page draws one of its own. */}
          {!controlled && (
            <div className="relative flex-1 lg:max-w-md">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search service name or HSC code (e.g. 996712, Air Freight)…"
              className={`${fieldClass} pl-9 pr-9`}
            />
            {search && (
              <button type="button" onClick={() => setSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                <X size={14} />
              </button>
            )}
          </div>
          )}
          <Pill tone="slate" className="lg:ml-auto self-start lg:self-auto">{filtered.length} of {rows.length}</Pill>
        </div>

        {loading ? (
          <div className="py-20 grid place-items-center">
            <Loader2 size={26} className="animate-spin text-blue-500" />
          </div>
        ) : (
          <Table
            head={[
              { label: 'S.No', width: '72px' }, { label: 'Service name' }, { label: 'HSC code' },
              { label: 'GST %' }, { label: 'Action' },
            ]}
            empty={rows.length === 0 ? 'No HSC codes yet — add the first one.' : 'No match for this search.'}
          >
            {filtered.map((row, i) => (
              <Row key={row.id}>
                <Cell className="text-slate-400 dark:text-slate-500 font-bold">{i + 1}</Cell>
                <Cell className="font-bold text-slate-900 dark:text-white">{row.label}</Cell>
                <Cell>
                  <span className="px-2.5 py-1 rounded-lg font-mono text-[12px] font-bold
                                   bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                    {row.hsn}
                  </span>
                </Cell>
                <Cell><Pill tone="blue">{GST_RATE}%</Pill></Cell>
                <Cell>
                  <div className="flex items-center gap-2">
                    <button type="button" className={btn.icon} title="Edit" onClick={() => openEdit(row)}>
                      <Pencil size={15} />
                    </button>
                    <button type="button" className={btn.iconDanger} title="Delete" onClick={() => setDeleting(row)}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                </Cell>
              </Row>
            ))}
          </Table>
        )}
      </Panel>

      <Panel className="mt-5 px-5 py-4">
        <div className="flex items-start gap-3">
          <span className="grid place-items-center w-9 h-9 rounded-xl flex-shrink-0
                           bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300">
            <ShieldCheck size={17} />
          </span>
          <div>
            <p className="text-[13.5px] font-bold text-slate-900 dark:text-white">Harmonised System code validation</p>
            <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-0.5 max-w-3xl">
              Every service bills at {GST_RATE}% GST. Changing a code here re-indexes the matching line item on
              every invoice raised from then on — invoices already generated keep the code they were issued with.
            </p>
          </div>
        </div>
      </Panel>

      {/* ── Add / edit ── */}
      {editing && (
        <Modal
          open
          onClose={closeForm}
          icon={Hash}
          tone="blue"
          title={editing.id ? 'Edit HSC code' : 'Add HSC code'}
          subtitle="Shows up in the invoice Description list."
          footer={(
            <div className="flex flex-col-reverse sm:flex-row gap-2.5">
              <button type="button" onClick={closeForm} disabled={saving} className={`${btn.quiet} disabled:opacity-50`}>
                Cancel
              </button>
              <button type="submit" form="hsc-form" disabled={saving} className={`${btn.primary} flex-1 justify-center disabled:opacity-60`}>
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
                {editing.id ? 'Save changes' : 'Add HSC code'}
              </button>
            </div>
          )}
        >
          <form id="hsc-form" onSubmit={submit} noValidate className="space-y-4">
            {field('label', 'Service name', {
              wide: true,
              input: { placeholder: 'e.g. AIR FREIGHT', autoComplete: 'off' },
              transform: (v) => v.toUpperCase(),
            })}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {field('hsn', 'HSC code', {
                input: { inputMode: 'numeric', placeholder: '996531', maxLength: 8, autoComplete: 'off' },
                transform: (v) => v.replace(/\D/g, ''),
              })}
              <FormField label="GST %" hint="(fixed)">
                <div className="h-11 px-3.5 rounded-xl flex items-center text-[13px] font-bold
                                bg-slate-100 border border-slate-200 text-slate-700
                                dark:bg-slate-800/70 dark:border-slate-700 dark:text-slate-200">
                  {GST_RATE}%
                </div>
                <p className="mt-1.5 text-[11.5px] text-slate-500 dark:text-slate-400">Same for every service.</p>
              </FormField>
            </div>

            {errors.api && (
              <div className="px-3.5 py-2.5 rounded-xl text-[13px] font-semibold
                              border border-rose-200 bg-rose-50/70 text-rose-600
                              dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300">
                {errors.api}
              </div>
            )}
          </form>
        </Modal>
      )}

      {/* ── Delete confirmation ── */}
      {deleting && (
        <ConfirmModal
          open
          busy={deletingBusy}
          onClose={() => setDeleting(null)}
          onConfirm={confirmDelete}
          title="Delete this HSC code?"
          subtitle={`${deleting.label} · HSC ${deleting.hsn}`}
          confirmLabel="Delete code"
        >
          <p className="text-[13px] text-slate-600 dark:text-slate-300">
            It stops appearing in the invoice Description list from now on. Invoices already saved with it
            keep the code they were issued with.
          </p>
        </ConfirmModal>
      )}
    </div>
  );
};

export default HsnCodeManager;
